import {
  ChefHat, Shirt, Sparkles, Home as HomeIcon, ShoppingBag, HeartPulse,
  Brain, Cpu, UserCheck, Eye, Wand2, Activity, MessageCircle,
} from "lucide-react";

// CookSorted and LookSorted are live on the Play Store; other products are pre-launch.
export const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.cooksorted.recipe";
export const LOOKSORTED_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.naari.looksorted";

export const PRODUCTS = [
  {
    slug: "cooksorted",
    name: "CookSorted",
    icon: ChefHat,
    iconImage: "/assets/cooksorted-icon.png",
    emoji: "🍲",
    available: true,
    appUrl: GOOGLE_PLAY_URL,
    tagline: "Plan meals. Balance nutrition. Generate groceries.",
    lines: ["Plan meals.", "Balance nutrition.", "Generate groceries."],
    accent: "coral",
    description:
      "CookSorted decides what to cook based on your pantry, preferences, nutrition goals and the week ahead — then builds your grocery list automatically.",
    features: [
      "Personalized weekly meal plans",
      "Nutrition balancing across the week",
      "Auto-generated smart grocery lists",
      "Adapts to diet, budget and family size",
    ],
    screenshots: [
      "/assets/cooksorted-screen-1.webp",
      "/assets/cooksorted-screen-2.webp",
      "/assets/cooksorted-screen-3.webp",
      "/assets/cooksorted-screen-4.webp",
      "/assets/cooksorted-screen-5.webp",
    ],
    screenshotsCaption:
      "Swipe through the app — plan meals, track what you cook, and auto-build your grocery list.",
    image:
      "https://images.pexels.com/photos/37470684/pexels-photo-37470684.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "looksorted",
    name: "LookSorted",
    icon: Shirt,
    iconImage: "/assets/looksorted-icon.png",
    emoji: "👗",
    available: true,
    appUrl: LOOKSORTED_PLAY_URL,
    tagline: "AI wardrobe. Daily outfits. Shopping intelligence.",
    lines: ["AI wardrobe.", "Daily outfits.", "Shopping intelligence."],
    accent: "gold",
    description:
      "LookSorted turns your wardrobe into an intelligent assistant — suggesting daily outfits based on weather, occasion and style, and guiding smarter shopping.",
    features: [
      "Digitize and organize your wardrobe",
      "Weather & occasion-aware outfit suggestions",
      "Shopping intelligence to fill real gaps",
      "Learns your evolving personal style",
    ],
    screenshots: [
      "/assets/looksorted-screen-1.webp",
      "/assets/looksorted-screen-2.webp",
      "/assets/looksorted-screen-3.webp",
      "/assets/looksorted-screen-4.webp",
      "/assets/looksorted-screen-5.webp",
      "/assets/looksorted-screen-6.webp",
    ],
    screenshotsCaption:
      "Swipe through the app — organize your wardrobe, get daily outfit ideas, and shop the gaps.",
    image:
      "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwZWxlZ2FudCUyMHdhcmRyb2JlJTIwZmFzaGlvbnxlbnwwfHx8fDE3ODM0MDg1OTN8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "naari-companion",
    name: "Naari Companion",
    icon: MessageCircle,
    iconImage: "/assets/naari-companion-icon.png",
    emoji: "💬",
    available: true,
    appUrl: "https://play.google.com/store/apps/details?id=com.naari.companion&hl=en_IN",
    tagline: "Naari is your AI companion for everyday life.",
    lines: ["Talk.", "Think out loud.", "Come back to."],
    accent: "coral",
    cardDescription:
      "Naari is your AI companion for everyday life — someone to talk to, think out loud with, and come back to, day after day.",
    description:
      "Naari is your AI companion for everyday life — someone to talk to, think out loud with, and come back to, day after day. Whether you're figuring out dinner, untangling a difficult feeling, planning your week, or just want to talk through your day, Naari listens, responds thoughtfully, and remembers what matters to you — so every conversation builds on the last, instead of starting from zero.",
    features: [
      "Chat anytime, about anything on your mind",
      "Pick up old conversations or start new ones — multiple threads, easy to find with search",
      "Naari responds in a warm, grounded voice — never clinical, never generic",
      "Delete any conversation permanently, anytime, right from the chat list",
    ],
    screenshots: [
      "/assets/naari-companion-screen-1.webp",
      "/assets/naari-companion-screen-2.webp",
      "/assets/naari-companion-screen-3.webp",
      "/assets/naari-companion-screen-4.webp",
      "/assets/naari-companion-screen-5.webp",
    ],
    screenshotsCaption:
      "Swipe through the app — tell Naari once, and she keeps it in mind.",
    image:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=940&h=650",
  },
  {
    slug: "homesorted",
    name: "HomeSorted",
    icon: HomeIcon,
    emoji: "🏠",
    available: false,
    tagline: "Manage your home. Tasks. Maintenance.",
    lines: ["Manage your home.", "Tasks.", "Maintenance."],
    accent: "coral",
    description:
      "HomeSorted keeps your household running — tracking chores, maintenance schedules and everything due around the house, before it becomes a problem.",
    features: [
      "Smart household task management",
      "Maintenance reminders & schedules",
      "Shared to-dos for the whole family",
      "Predicts what needs attention next",
    ],
    image:
      "https://images.unsplash.com/photo-1484154218962-a197022b5858?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0fDB8MXxzZWFyY2h8Mnx8bWluaW1hbCUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODM0MDg1OTN8MA&ixlib=rb-4.1.0&q=85",
  },
];

export const DECISION_CARDS = [
  { emoji: "🍳", q: "What should I cook today?", product: "CookSorted" },
  { emoji: "👗", q: "What should I wear today?", product: "LookSorted" },
  { emoji: "✨", q: "How do I care for my skin?", product: "GlowSorted" },
  { emoji: "🛒", q: "What should I buy?", product: "General" },
  { emoji: "🏠", q: "How do I manage my home?", product: "HomeSorted" },
  { emoji: "❤️", q: "How do I care for myself?", product: "General" },
];

export const DECISION_FLOW = [
  "AI Recommendations",
  "Personal Preferences",
  "Family Context",
  "Weather",
  "Calendar",
  "Habits",
];

export const WHY_NAARI = [
  { title: "Personalized", icon: UserCheck, text: "Every recommendation is built around you — your tastes, your routines, your life." },
  { title: "Intelligent", icon: Brain, text: "AI learns from your habits and gets sharper the more you live with it." },
  { title: "Everyday", icon: Activity, text: "Designed for daily use — not occasional use. It shows up when it matters." },
];

export const TECHNOLOGY = [
  { title: "Artificial Intelligence", icon: Brain },
  { title: "Machine Learning", icon: Cpu },
  { title: "Personalization", icon: UserCheck },
  { title: "Computer Vision", icon: Eye },
  { title: "Recommendation Systems", icon: Wand2 },
  { title: "Behavioral Intelligence", icon: Activity },
];

export const ROADMAP = [
  { year: "2026", items: ["CookSorted", "LookSorted", "Naari AI"], note: "The first wave of daily-decision products." },
  { year: "2027", items: ["HomeSorted", "ShopSorted", "CareSorted"], note: "Expanding across the whole household." },
  { year: "Future", items: ["GlowSorted"], note: "Personalized skin, hair and beauty that learns and evolves with you." },
];

// Brand icon for every roadmap item so the timeline stays visually consistent.
export const ROADMAP_ICONS = {
  CookSorted: ChefHat,
  LookSorted: Shirt,
  GlowSorted: Sparkles,
  HomeSorted: HomeIcon,
  ShopSorted: ShoppingBag,
  CareSorted: HeartPulse,
  "Naari AI": Brain,
};

export const TEAM = [
  {
    name: "Ambalika Smiti",
    label: "Founder",
    linkedin: "https://www.linkedin.com/in/ambalika-smiti/",
    photo: "/assets/founder-ambalika.jpeg",
    bio: [
      "Built analytics organizations.",
      "Led data teams.",
      "Built AI products.",
      "Passionate about simplifying daily decisions.",
    ],
  },
  {
    name: "Karishma Kapadia",
    label: "Co-Founder",
    linkedin: "https://www.linkedin.com/in/kdkarishma/",
    photo: "/assets/cofounder-karishma.jpg",
    bio: [
      "Ex-Microsoft Engineering Manager with 17+ years of experience.",
      "Specializes in Agentic AI and UX innovation.",
      "Experienced in Android, Kotlin, and Jetpack Compose development.",
      "Passionate about simplifying daily decisions.",
    ],
  },
];

// Back-compat alias (the founder is the first team member).
export const FOUNDER = TEAM[0];

export const BLOG_POSTS = [
  {
    slug: "decision-fatigue",
    title: "The Hidden Cost of Decision Fatigue",
    category: "Decision Intelligence",
    date: "Jan 2026",
    excerpt: "We make thousands of micro-decisions daily. Here's why that quietly drains you — and how AI can help.",
    read: "5 min read",
  },
  {
    slug: "ai-that-decides",
    title: "From Search to Decide: The Next Shift in AI",
    category: "AI",
    date: "Jan 2026",
    excerpt: "Search gave us information. The next generation of AI gives us decisions. Here's what changes.",
    read: "6 min read",
  },
  {
    slug: "everyday-os",
    title: "Why the World Needs an Operating System for Everyday Life",
    category: "Vision",
    date: "Dec 2025",
    excerpt: "Apps solve single problems. Life isn't single-problem. A look at the case for a unified everyday-life OS.",
    read: "7 min read",
  },
  {
    slug: "personalization-nutrition",
    title: "Personalization Done Right: Nutrition That Fits Your Week",
    category: "Nutrition",
    date: "Dec 2025",
    excerpt: "Good nutrition isn't a rigid plan — it's one that bends around your real, messy week.",
    read: "4 min read",
  },
];

export const NAV_LINKS = [
  { label: "Products", to: "/products" },
  { label: "Vision", to: "/vision" },
  { label: "About", to: "/about" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];
